import { JQueryInput } from "./types";
import { registerEvents } from "./skyward.ui.events";
import { getUnmaskedValue, addMask, setValue as dataMaskSetValue, applyYearMask } from "./skyward.ui.datamask";
import { alignTitles } from "./skyward.ui.fieldset";

const skyward = window.skyward;

// This file will attempt to restore state to data bound input, select, eel, and codeeel type controls
export let getValueToString = function (element: JQueryInput)
    {
        const val = getValue(element);
        return (typeof val.safeToDateString === "function") ? val.safeToDateString() : val.toString();
    };

export let getValue = function (elementArg: JQueryInput)
    {
        const element = $(elementArg);

        const elementData = element.data();
        let val;

        if (element.is(":radio"))
        {
            if (element.is(":checked"))
            {
                val = element.val();
            }
        }
        else if (element.is(":checkbox"))
        {
            if (element.hasClass("valuecheckbox"))
            {
                if (element.is(":checked"))
                {
                    val = element.val();
                }
            }
            else
            {
                val = element.is(":checked");
            }
        }
        else if (element.hasClass("masked"))
        {
            val = getUnmaskedValue(element);
        }
        else if (element.hasClass("relativeDate"))
        {
            val = elementData.relativeDate;
        }
        else if (element.is("[type=date]")) //currently only in mobile access
        {
            // value will be an ISO-formatted date (YYYY-MM-DD) or an empty string
            const value = element.val() as string;
            // Date-only values are interpreted as UTC, but date+time is interpreted as local time.
            let newVal = value ? skyward.time.parseDate(value + "T00:00:00") : new Date(NaN);

            // Temp fix for issue with iOS 14 (not able to trigger date picker) (as of 11/16/2020)
            if (newVal.toString() === new Date(NaN).toString())
            {
                newVal = skyward.datepicker.parseDateString(value)
            }
            return newVal;
        }
        else if (element.is("[type=time]"))
        {
            val = skyward.timePicker.sanitizeInput(element.val() as string);
        }
        else if (element.hasClass("datepicker"))
        {
            val = skyward.datepicker.parseDateString(element.val() as string);
        }
        else if (element.closest('.quickEntryText').length !== 0)
        {
            val = element.closest('.quickEntryText').find('.codeEELDescription').encodedVal();
        }
        else if (element.hasClass('codeEEL'))
        {
            val = element.find('.codeEELValue').val();
        }
        else if (element.hasClass('tileViewEEL'))
        {
            val = element.find('.tileViewEEL_value').val();
        }
        else if (element.hasClass('htmlComboBox'))
        {
            val = element.find('.htmlComboBox_value').val();
        }
        else if (element.is(".radioButtonGroupValue,.radioButtonGroup"))
        {
            val = skyward.radioButtonGroup.getValue(element);
        }
        else if (element.hasClass('dmsBackingData'))
        {
            val = skyward.distinctmultiselecteel.getValue(element);
        }
        else if (element.hasClass('multiSelectBackingData'))
        {
            val = skyward.multipleselectbox.getValue(element);
        }
        else if (element.hasClass('raceEthnicityVerification'))
        {
            val = skyward.onlineform.raceEthnicityGetValue(element);
        }
        else if (element.hasClass("button") && element.hasClass("useChangeTracking"))
        {
            val = elementData.value;
        }
        else if (element.hasClass('colorPicker'))
        {
            val = element.spectrum('get').toString(elementData.colorFormat).replace(/\s/g, '');
        }
        else if (element.hasClass("parametermapper"))
        {
            val = skyward.parametermapper.getValue(element);
        }
        else if (element.hasClass("ckeditor"))
        {
            if (CKEDITOR.instances[element.attr("id")!])
            {
                val = skyward.extensions.encodeHtml(CKEDITOR.instances[element.attr("id")!].getData());
            }
            else
            {
                val = element.encodedVal();
            }
        }
        else if (element.hasClass("dashboardItemSizer__inner") || element.hasClass("dashboardItemSizer__container"))
        {
            val = element.find("#DashboardItemSize").val();            
        }
        else
        {
            if (element.val() === null)
            {
                val = null;
            }
            else
            {
                val = element.encodedVal();
            }
        }

        return val;
    };

    // this can be used when you need to use the value as part of a larger object that will be encoded
export let getUnencodedValue = function (elementArg: JQueryInput)
    {
        const element = $(elementArg);
        const value = getValue(element);

        if (value == null)
        {
            return null;
        }

        if (value instanceof Date)
        {
            if (isNaN(value.getTime()))
            {
                return null;
            }
            return skyward.time.formatServerDate(value, !element.hasClass("datepicker"));
        }

        if (Array.isArray(value))
        {
            return value.map(v => skyward.extensions.decodeHtml(v));
        }

        if (typeof value === "object")
        {
            const unencodedObject: { [key: string]: string } = {};
            for (const key in value)
            {
                const unencodedKey = skyward.extensions.decodeHtml(key);
                unencodedObject[unencodedKey] = skyward.extensions.decodeHtml(value[key]);
            }
            return unencodedObject;
        }

        return skyward.extensions.decodeHtml(value as string);
    };

export let getOriginalValue = function (elementArg: JQueryInput)
    {
        const element = $(elementArg);

        const elementData = element.data();
        let val;

        if (element.hasClass("datepicker"))
        {
            // We don't know if this was an absolute or relative datepicker originally, so try both
            val = skyward.time.parseDate(element.data("original-value"));
            if (!skyward.datepicker.isValidDateObject(val) && element.hasClass("hasRelativeDatePicker"))
            {
                val = element.data("originalRelativeDate");
            }
        }
        else if (element.is(":checkbox") && element.hasClass("valuecheckbox"))
        {
            val = $.inArray(element.attr("data-original-value"), ["true", "indeterminate"]) === -1 ? undefined : element.attr("data-original-value");
        }
        else if (element.is("[data-mask]"))
        {
            const originalValueMasked = element.attr("data-original-value");
            const copy = element.clone();
            addMask(copy, elementData.mask);
            dataMaskSetValue(copy, originalValueMasked);
            val = getUnmaskedValue(copy);
        }
        else if (element.is(".radioButtonGroupValue,.radioButtonGroup"))
        {
            val = skyward.radioButtonGroup.getOriginalValue(element);
        }
        else if (element.hasClass('dmsBackingData'))
        {
            val = skyward.distinctmultiselecteel.getOriginalValue(element);
        }
        else if (element.hasClass('multiSelectBackingData'))
        {
            val = skyward.multipleselectbox.getOriginalValue(element);
        }
        else if (element.hasClass("listSortOrder"))
        {
            val = JSON.stringify(element.data("originalValue"));
        }
        else if (element.hasClass('codeEEL'))
        {
            val = element.find('.codeEELValue').data("original-value");
        }
        else
        {
            // Using data() automatically runs a JSON parse on any string beginning with "{", so any text field that contains valid JSON cannot be
            // directly compared with its current value.
            // *Setting* something via data() doesn't set the attribute to match, so we check if the data() value could have come from the
            // data-original-value attribute, and return its value if so.
            val = element.data("original-value");
            if (val instanceof Object && val !== null)
            {
                try
                {
                    const originalData = element.attr("data-original-value");
                    const parsed = JSON.parse(originalData || "");
                    if (skyward.global.deepEquals(parsed, val))
                    {
                        val = originalData;
                    }
                }
                catch (_error) { /* JSON parse failed */ }
            }
        }

        return val;
    };

export let setDataAttribute = function (elementArg: JQueryInput, attribute: string | undefined, value: any)
    {
        const element = $(elementArg);

        //Check to see if attribute exists, don't add a new one
        if (attribute !== undefined && typeof element.data(attribute) !== 'undefined')
        {
            element.data(attribute, value);
        }
    };

export let setValue = function (elementArg: JQueryInput, value: any, suppressChangeEvent = false)
    {
        const element = $(elementArg);

        if (!suppressChangeEvent && element.hasClass("suppressChangeEvent"))
        {
            suppressChangeEvent = true;
        }

        if (element.is("img"))
        {
            element.prop("src", value);
            if (!suppressChangeEvent)
            {
                element.trigger("change");
            }
        }
        else if (element.is(":radio"))
        {
            for (let i = 0; i < element.length; i++)
            {
                const radioButton = $(element[i]);
                if (radioButton.val() == value)
                {
                    radioButton.prop("checked", true);
                    if (!suppressChangeEvent)
                    {
                        radioButton.trigger("change");
                    }
                }
            }
        }
        else if (element.hasClass("radioSet"))
        {
            element.find(':radio').each(function ()
            {
                const input = $(this);
                if (input.val() == value)
                {
                    if (input.prop("checked") != true)
                    {
                        input.prop("checked", true);
                        if (!suppressChangeEvent)
                        {
                            input.trigger("change");
                        }
                    }
                }
                else
                {
                    if (input.prop("checked") != false)
                    {
                        input.prop("checked", false);
                        if (!suppressChangeEvent)
                        {
                            input.trigger("change");
                        }
                    }
                }
            });
        }
        else if (element.is(":checkbox"))
        {
            element.prop("checked", typeof value === "string" ? (value.toLowerCase() === "true") : value);

            if (!suppressChangeEvent)
            {
                element.trigger("change");
            }
        }
        else if (element.is("[type=date]"))
        {
            if (!value)
            {
                element.val('');
                return;
            }
            const date = skyward.time.parseDate(value);
            const year = date.getFullYear();
            const month = ("0" + (date.getMonth() + 1)).slice(-2);
            const day = ("0" + date.getDate()).slice(-2);
            element.val([year, month, day].join('-'));
        }
        else if (element.hasClass("datepicker"))
        {
            const datepickerDate = skyward.time.parseDate(value);
            if (skyward.datepicker.isValidDateObject(datepickerDate))
            {
                element.val($.datepicker.formatDate(element.hasClass("browseSearchBox") ? "mm/dd/yy" : "mm/dd/yy DD", datepickerDate));
            }
            else
            {
                if (element.hasClass("hasRelativeDatePicker"))
                {
                    const relativeDate = skyward.relativedatepicker.parseRelativeDateString(value);
                    if (relativeDate !== null)
                    {
                        const datepickerId = element.attr("id")!;
                        skyward.relativedatepicker.setValue(datepickerId, relativeDate);
                        if (!suppressChangeEvent)
                        {
                            skyward.relativedatepicker.getRelativeDateDisplay(datepickerId).trigger("change");
                        }
                        return;
                    }
                }
                element.val("");
            }

            if (!suppressChangeEvent)
            {
                element.trigger("change");
            }
        }
        else if (element.is("label"))
        {
            element.text(value);
            if (!suppressChangeEvent)
            {
                alignTitles(element);
                element.trigger("change");
            }
        }
        else if (element.is('[type=time]'))
        {
            if (!value)
            {
                element.val('');
                return;
            }
            let hour: number | undefined, minute: number | undefined, isPm: boolean | undefined;
            const time = skyward.time.parseDate(value);

            if (!isNaN(time.getTime()))
            {
                hour = time.getHours();
                minute = time.getMinutes();
            }
            else if (value)
            {
                value.split(" ").map(function (x: string)
                {
                    if (x.indexOf(':') === 2)
                    {
                        const timeParts = x.split(':');
                        hour = parseInt(timeParts[0]);
                        minute = parseInt(timeParts[1]);
                    }
                    else
                    {
                        isPm = x.toLowerCase() === 'pm';
                    }
                });

                if (isPm && hour && !isNaN(hour))
                {
                    hour += 12;
                }
            }

            value = ("0" + hour).slice(-2) + ':' + ("0" + minute).slice(-2);
            element.val(value);

        }
        else if (element.hasClass("codeEEL"))
        {
            skyward.codeeel.setCodeEELValue(element, value, suppressChangeEvent);
        }
        else if (element.hasClass("tileViewEEL"))
        {
            const searchBox = element.find(".tileViewEEL_search");

            $.ajax(
                {
                    url: skyward.global.virtualDir + 'common' + "/" + 'tilevieweel' + "/validateselection/" + value,
                    type: "POST",
                    data: skyward.tilevieweel.getPostData(searchBox.data(), searchBox, value),
                    success: function (data)
                    {
                        skyward.tilevieweel.populateBoxes(element, data, value);
                    }
                });

        }
        else if (element.hasClass("htmlComboBox"))
        {
            skyward.htmlcombobox.populateBoxes(element, value);
        }
        else if (element.hasClass('radioButtonGroupValue') || element.hasClass('radioButtonGroup'))
        {
            skyward.radioButtonGroup.setValue(element, value, suppressChangeEvent);
        }
        else if (element.hasClass("dmsBackingData"))
        {
            skyward.distinctmultiselecteel.setValue(element, value, suppressChangeEvent);
        }
        else if (element.hasClass('multiSelectBackingData'))
        {
            skyward.multipleselectbox.populate(element, value, suppressChangeEvent);
        }
        else if (element.hasClass("dashboardItemSizer__inner") || element.hasClass("dashboardItemSizer__container"))
        {
            const dashboardItemSize = element.find("#DashboardItemSize");
            dashboardItemSize.val(value);
            if (!suppressChangeEvent)
            {
                dashboardItemSize.trigger("change");
            }
        }
        else if (element.hasClass("filterViewValue"))
        {
            remask(element, value);

            if (value === null || value === "")
            {
                element.data("filterCondition", "");
                skyward.filterbuilder.updateFilterViewerDisplay(element);
                if (!suppressChangeEvent)
                {
                    element.trigger("change");
                }
            }
            else
            {
                $.ajax(
                    {
                        url: skyward.global.virtualDir + "SkySys/Filter/ConvertToJSON",
                        type: "POST",
                        dataType: "JSON",
                        data:
                        {
                            xml: skyward.extensions.encodeHtml(value)
                        },
                        success: function (data)
                        {
                            element.data("filterCondition", data || "");
                            skyward.filterbuilder.updateFilterViewerDisplay(element);
                            if (!suppressChangeEvent)
                            {
                                element.trigger("change");
                            }
                        }
                    });
            }
        }
        else
        {
            if (element.data("spectrum.id") !== undefined)  //Check if it's an initialized ColorPicker input
            {
                $(element).spectrum("set", value);
            }
            else  //All other elements
            {
                remask(element, value);
            }

            if (!suppressChangeEvent)
            {
                element.trigger("change");
            }
        }
    };

export let disable = function (control: JQueryInput, idPrefix?: string)
    {
        return toggle(control, false, idPrefix);
    };

export let enable = function (control: JQueryInput, idPrefix?: string)
    {
        return toggle(control, true, idPrefix);
    };

export let toggle = function (controlArg: JQueryInput, isEnabled: boolean, idPrefix?: string)
    {
        let calculateControl = controlArg;
        if (!(calculateControl instanceof $))
        {
            calculateControl = $(calculateControl);
            if (calculateControl.length === 0 && typeof controlArg === "string" && idPrefix && idPrefix.length > 0)
            {
                calculateControl = $(controlArg.replace(/#/g, `#${idPrefix}`)) as JQuery<HTMLElement>;
            }
        }

        const control = $(calculateControl);

        let elements = $();
        let container;

        if (control.is(".codeEELValue,.tileViewEEL_value,.selectorEELInput"))
        {
            elements = elements.add(control.closest(".codeEEL,.tileViewEEL,.selectorEEL").find(".eELInputContainer__textBox,.ui-control-container,.eELInputContainer__button"));
        }
        else if (control.hasClass("htmlComboBox_value"))
        {
            container = control.closest(".htmlComboBox");
            elements = elements.add(container);
            elements = elements.add(container.find(".htmlComboBox_input,.htmlComboBox_button"));
        }
        else if (control.hasClass("dmsBackingData"))
        {
            container = control.closest(".distinctMultiSelectEEL");
            elements = elements.add(container);
            elements = elements.add(container.find(".ui-control-container,.distinctMultiSelectUserInput"));
        }
        else if (control.hasClass("datepicker"))
        {
            elements = elements.add(control);
            control.datepicker("option", "disabled", !isEnabled);
            control.siblings('.datepicker-trigger').toggle(isEnabled);
            if (control.hasClass("hasRelativeDatePicker"))
            {
                elements = elements.add(control.closest(".datepicker-wrapper").siblings('.relativeDateDisplay'));
                control.closest(".datepicker-wrapper").siblings(".launchRelativeDatePicker").toggle(isEnabled);
            }
        }
        else if (control.is(".timePicker,.timePickerBox"))
        {
            elements = elements.add(control.closest(".timePicker").find(".timePickerBox,.timePickerButton"));
        }
        else if (control.is(".radioButtonGroupValue,.radioButtonGroup"))
        {
            elements = elements.add(control.find(":input"));
            if (isEnabled)
            {
                skyward.radioButtonGroup.enable(control);
            }
            else
            {
                skyward.radioButtonGroup.disable(control);
            }
        }
        else if (control.is(".radioButtonContent"))
        {
            elements = elements.add(control);
            if (isEnabled)
            {
                $.each(control, function ()
                {
                    $(this).parent().removeClass("disabled");
                });
            }
            else
            {
                $.each(control, function ()
                {
                    $(this).parent().addClass("disabled");
                });
            }
        }
        else if (control.hasClass('colorPicker'))
        {
            elements = elements.add(control);
            control.spectrum({ disabled: !isEnabled });
            const picker = control.next(".sp-skyward");
            if (picker.length > 0)
            {
                picker.attr("tabindex", isEnabled ? 0 : -1);
            }
        }
        else if (control.is('a'))
        {
            control.css({ "pointer-events": isEnabled ? "" : "none" });
            elements = elements.add(control);
        }
        else if (control.is(".codeEELSelectedValueDisplayField"))
        {
            // do nothing.  This control shouldn't be toggled.
            // This is to work around a bug with dependency.
        }
        else if (control.is(':input'))
        {
            elements = elements.add(control);
        }
        else if (control.hasClass('checkboxGroup'))
        {
            if (control.parent().is('fieldSet'))
            {
                elements = control.parent();
            }
        }

        if (elements.length)
        {
            elements.prop("readonly", !isEnabled);
            elements.prop("disabled", !isEnabled);
            elements.toggleClass("ui-disabled", !isEnabled);
            elements.attr("aria-disabled", (!isEnabled).toString());
            const defaultTabIndex = "0";

            //prevent focusing on disabled elements
            elements.filter(":input,a").each(function ()
            {
                const element = $(this);
                let origTabIndex = element.data("origTabIndex");
                if (typeof origTabIndex === "undefined")
                {
                    if (element.is(":input"))
                    {
                        origTabIndex = defaultTabIndex;
                    }
                    else
                    {
                        origTabIndex = element.attr("tabindex") || defaultTabIndex;
                    }
                    element.data("origTabIndex", origTabIndex);
                }
                element.attr("tabindex", isEnabled ? origTabIndex : "-1");
            });

            elements.trigger("readonly", isEnabled);
        }

        //return true if the passed in control matched a specific element type
        return elements.length > 0;
    };

export let hide = function (identifier: JQueryInput, animate?: boolean, idPrefix?: string)
    {
        const control = getControlToShowOrHide(identifier, idPrefix);
        if (!control || !control.get(0))
        {
            return;
        }

        if (control.is(".radioButtonGroupValue,.radioButtonGroup"))
        {
            skyward.radioButtonGroup.hide(control);
        }
        else if (control.hasClass("datepicker"))
        {
            skyward.datepicker.hide(control, animate);
        }
        else if (control.hasClass("timePickerBox"))
        {
            skyward.timePicker.hide(control, animate);
        }
        else if (control.is("option,optgroup"))
        {
            toggleOption(control, false);
        }
        else if (control.is(".codeEELValue,.tileViewEEL_value,.selectorEELInput"))
        {
            const container = control.closest(".codeEEL,.tileViewEEL,.selectorEEL");
            if (animate)
            {
                container.animateClose();
            }
            else
            {
                container.hide();
            }
        }
        else if (control.hasClass("contentMessageArea"))
        {
            control.addClass("cmaHidden");
        }
        else
        {
            if (animate !== false)
            {
                control.animateClose();
            }
            else
            {
                control.hide();
            }
            control.trigger("visible", false);
        }
    };

export let show = function (identifier: JQueryInput, animate?: boolean, idPrefix?: string)
    {
        const control = getControlToShowOrHide(identifier, idPrefix);
        if (!control || !control.get(0))
        {
            return;
        }
        let animationControl;

        if (control.is(".radioButtonGroupValue,.radioButtonGroup"))
        {
            animationControl = skyward.radioButtonGroup.show(control);
        }
        else if (control.hasClass("datepicker"))
        {
            animationControl = skyward.datepicker.show(control, animate);
        }
        else if (control.hasClass("timePickerBox"))
        {
            animationControl = skyward.timePicker.show(control, animate);
        }
        else if (control.is("option,optgroup"))
        {
            toggleOption(control, true);
            animationControl = control;
        }
        else if (control.is(".codeEELValue,.tileViewEEL_value,.selectorEELInput"))
        {
            const container = control.closest(".codeEEL,.tileViewEEL,.selectorEEL");
            if (animate)
            {
                container.animateOpen();
            }
            else
            {
                container.show();
            }
            animationControl = container;
        }
        else if (control.hasClass("contentMessageArea"))
        {
            control.removeClass("cmaHidden");
            animationControl = control
        }
        else
        {
            if (animate !== false)
            {
                control.animateOpen();
            }
            else
            {
                control.show();
            }
            animationControl = control;
        }
        animationControl.trigger("visible", true);
        animationControl.animationPromise().done(function ()
        {
            skyward.events.processVisibleActions(control);
        });
    };

export let toggleVisible = function (identifier: JQueryInput, visible: boolean, animate: boolean = true, idPrefix?: string)
    {
        if (visible)
        {
            show(identifier, animate, idPrefix);
        }
        else
        {
            hide(identifier, animate, idPrefix);
        }
    };

export let setComboBoxOptions = function (comboboxIdentifier: string | JQuery<HTMLElement>, options: any[], idPrefix?: string)
    {
        let comboBox = typeof comboboxIdentifier == "string" ? $(`[name='${comboboxIdentifier}']`) : comboboxIdentifier;

        if (comboBox.length === 0)
        {
            comboBox = $(`[id="${comboboxIdentifier}"]`);
        }
        if (comboBox.length === 0 && idPrefix && idPrefix.length > 0)
        {
            comboBox = $(`[id="${idPrefix}${comboboxIdentifier}"]`);
        }
        comboBox.empty();

        //options must be an Array of objects to maintain sort order
        $.each(options, function (_index, value)
        {
            if (typeof value.Value == "string")
            {
                comboBox.append($("<option></option>").attr("value", value.Key).text(value.Value));
            }
            else
            {
                // If the value is not a string it will be an object that holds the value, selected, and dependents
                const option = $("<option></option>").attr("value", value.Key).text(value.Value.Value);

                if (value.Value.Dependents)
                {
                    option.attr("data-dependents", JSON.stringify(value.Value.Dependents));
                }

                if (value.Value.Selected)
                {
                    option.attr("Selected", "Selected");
                }

                comboBox.append(option);
            }
        });
    };

export let getOriginalValuesData = function (selector: string)
    {
        const controls = getCurrentValuesData(selector);
        return { controls: JSON.stringify(controls) };
    };

export let getEditableFields = function (selector: JQuery<HTMLElement>)
    {
        const hashList: { [key: string]: string | number | string[] | undefined } = {};
        selector.find(".editableFields").each(function ()
        {
            const hashContainer = $(this);
            const hash = hashContainer.data("hash");
            const fields = hashContainer.val();
            hashList[hash] = fields;
        });
        return JSON.stringify(hashList);
    };

export let valueHasChanged = function (elementArg: JQueryInput)
    {
        const element = $(elementArg);

        const value = getValue(element);
        const data = element.data();

        if ($.isEmptyObject(data))
        {
            return false;
        }

        return data.originalValue != value;
    };

export let getCurrentValuesData = function (selector: string)
    {
        type ValueData = {
            key: string,
            value: any,
            skip: boolean
        }

        type ValueDataItem = {
            id: string,
            items: Array<ValueData>
        }

        const list: Array<ValueDataItem> = [];

        $(selector + " [data-original-value]").each(function ()
        {
            const item = $(this as HTMLElement);
            const name = item.attr("name");

            if (name != null)
            {
                let value = null;

                // Workflow refresh is the only time we save off the control state
                // On AJAX we remove all workflow control state information from pagestate
                if (selector == ".workflowPopup")
                {
                    const data = item.data();

                    if (item.is(":radio"))
                    {
                        value = $(selector + " input[name='" + name + "']:checked").val();
                    }
                    else if (item.is(":checkbox"))
                    {
                        value = item.is(":checked");
                    }
                    else if (item.hasClass("masked"))
                    {
                        value = skyward.datamask.getUnmaskedValue(item);
                    }
                    else
                    {
                        value = item.encodedVal();
                    }

                    if (value != null && value == data.originalValue)
                    {
                        value = null;
                    }
                }
                list.push({
                    id: name,
                    items: [{
                        key: "value",
                        value: value,
                        skip: false
                    }]
                });
            }
        });

        return list;
    };

export let clearValue = function (elementArg: JQueryInput, suppressChangeEvent = false)
    {
        const element = $(elementArg);

        if (!suppressChangeEvent && element.hasClass("suppressChangeEvent"))
        {
            suppressChangeEvent = true;
        }

        if (element.is(":checkbox"))
        {
            element.prop("checked", false);
            if (!suppressChangeEvent)
            {
                element.trigger("change");
            }
        }
        else if (element.hasClass("datepicker"))
        {
            element.val('');
            if (!suppressChangeEvent)
            {
                element.trigger("change");
            }
        }
        else if (element.is("label"))
        {
            element.text('');
            if (!suppressChangeEvent)
            {
                element.trigger("change");
            }
        }
        else if (element.hasClass("codeEEL"))
        {
            const codeEELValue = element.find(".codeEELValue");
            const codeEELData = codeEELValue.data();
            const postData = { "securityHash": codeEELData.securityHash };

            codeEELValue.val('');
            $.ajax({
                url: skyward.global.virtualDir + codeEELData.area + "/" + codeEELData.controller + "/codeeel/" + '',
                type: "POST",
                data: postData,
                success: function (data)
                {
                    skyward.codeeel.populateBoxes(element, data, !suppressChangeEvent);
                }
            });

        }
        else if (element.hasClass('distinctMultiSelectEEL') || element.hasClass("dmsBackingData"))
        {
            skyward.distinctmultiselecteel.clearValue(element, suppressChangeEvent);
        }
        else if (element.hasClass('multiSelectBackingData'))
        {
            skyward.multipleselectbox.clearValue(element, suppressChangeEvent);
        }
        else if (element.hasClass("tileViewEEL"))
        {
            const searchBox = element.find(".tileViewEEL_search");
            const searchData = searchBox.data();
            skyward.tilevieweel.populateBoxes(element, searchData, "");
        }
        else if (element.hasClass("htmlComboBox"))
        {
            skyward.htmlcombobox.populateBoxes(element, null);
        }
        else if (element.hasClass('raceEthnicityVerification'))
        {
            element.find(':checkbox').prop('checked', false);
            element.trigger("change");
        }
        else if (element.hasClass('listSortOrder'))
        {
            element.val('[]');
            if (!suppressChangeEvent)
            {
                element.trigger("change");
            }
        }
        else
        {
            remask(element, '');
            if (!suppressChangeEvent)
            {
                element.trigger("change");
            }
        }
    };

export let changeId = function (elementArg: JQueryInput, idValue: string, nameValue: string)
    {
        const element = $(elementArg);

        element.attr('id', idValue);

        if (!nameValue)
        {
            nameValue = idValue;
        }

        if (element.attr('name'))
        {
            element.attr('name', nameValue);
        }

        if (element.hasClass("radioButtonGroup"))
        {
            element.data("name", nameValue);
            element.find("input[type='radio']").attr("name", nameValue + "child");
        }
    };

export let changeCheckBoxWithSelectAll = function (currentCheckBox: JQuery<HTMLElement>, isSelectAllChecked: boolean)
    {
        const currentState = currentCheckBox.is(':checked');

        if (currentCheckBox.hasClass('allowCodeChangesWhenReadonly') || (!currentCheckBox.is(':disabled') && !currentCheckBox.is('[readonly]')))
        {
            currentCheckBox.prop('checked', isSelectAllChecked);

            // Don't fire the onchange event if the checkbox was already checked
            // or if the checkbox was unchecked already
            if ((!currentState && isSelectAllChecked) || (currentState && !isSelectAllChecked))
            {
                currentCheckBox.trigger("change");
            }
        }
    };

export let getScopeContainer = function (control: JQueryInput)
    {
        return $(control).closest("#moduleContent, #detailsPanel, .workflowContent, body");
    };

registerEvents("controls",
{
    hideoption: function (this: JQueryInput) { toggleOption($(this), false); }
});

function remask(element: JQuery<HTMLElement>, value: string | number | string[] | undefined)
{
    if ((element.data("mask") || element.data("decimalMask")) && element.hasClass("masked"))
    {
        dataMaskSetValue(element, value);
    }
    else
    {
        element.val(value ?? "");
    }

    if (element.is("[data-year-mask]"))
    {
        applyYearMask(element);
    }
}

/**
 * Toggles the visible state of an <option> element.
 *
 * @param option - The option element to show or hide.
 * @param visible - True to show, false to hide.
 */
export function toggleOption(option: JQuery<HTMLElement>, visible: boolean)
{
    if (visible)
    {
        option.prop("disabled", false).css("display", "");
        const select = option.closest("select");
        if (select.children("option:not(:disabled)").length == 1)
        {
            setValue(select, option.val(), true);
        }
    }
    else
    {
        option.prop("disabled", true).css("display", "none");

        // Ensure the hidden option is not selected
        if (option.is(":selected"))
        {
            option.removeAttr("selected");
            const select = option.closest("select");
            const firstNonDisabledOption = select.find("option:not(:disabled)").first();
            setValue(select, firstNonDisabledOption.val(), true);
        }
    }
}

function getControlToShowOrHide(identifier: JQueryInput, idPrefix?: string)
    {
        let control: JQuery<Element> | null | undefined;

        if (typeof identifier === "string")
        {
            try
            {
                control = $('[name="' + identifier + '"]');
            }
            catch (ex)
            {
                control = null;
            }

            if (!control || control.length === 0)
            {
                try
                {
                    control = $("#" + identifier);
                    if (control.length === 0 && idPrefix && idPrefix?.length > 0)
                    {
                        control = $("#" + idPrefix + identifier);
                    }
                }
                catch (ex)
                {
                    control = null;
                }
            }
        }

        if (!control || control.length === 0)
        {
            try
            {
                control = $(identifier);
                if (control.length === 0 && typeof identifier === "string" && idPrefix && idPrefix?.length > 0)
                {
                    control = $(identifier.replace("#", `#${idPrefix}`));
                }
            }
            catch (ex)
            {
                control = null;
            }
        }
        return control as JQuery<HTMLElement>;
    }
